/* ef-nav.js — the header: mega-menu, sub-category tabs, phone drawer.
 *
 * Behaviour is the locked round-27 spec: opens on hover AND focus, closes on
 * header mouseleave, Escape, or navigation. Durations live in the CSS
 * (260ms panel, 45ms column stagger, 220ms caret) and are deliberately NOT
 * repeated here — one clock, in one place.
 *
 * Hover alone would make the menu unreachable by keyboard, and click alone
 * would make it feel broken to a mouse. It answers to both, and the open panel
 * is the same element either way so the two can never disagree.
 */
(function () {
  'use strict';

  var header = document.querySelector('[data-ef-header]');
  if (!header) return;

  var buttons = Array.prototype.slice.call(header.querySelectorAll('[data-ef-menu]'));
  var panels  = Array.prototype.slice.call(header.querySelectorAll('[data-ef-panel]'));
  var open = null;
  var closeTimer = null;

  function panelFor(key) {
    for (var i = 0; i < panels.length; i++) {
      if (panels[i].getAttribute('data-ef-panel') === key) return panels[i];
    }
    return null;
  }

  function show(key) {
    clearTimeout(closeTimer);
    /* Coming back mid-retract: cancel the pending hide and clear the class, or
       the panel reappears and is then hidden by a timer already in flight. */
    clearTimeout(exitTimer);
    exitTimer = null;
    panels.forEach(function (p) { p.classList.remove('is-closing'); });
    if (open === key) return;
    panels.forEach(function (p) { p.hidden = p.getAttribute('data-ef-panel') !== key; });
    /* The handoff's "backdrop blurs" is about the PAGE behind the panel, not
       the panel itself. CSS cannot select "some panel is open" from the page
       below, so the header carries the state and the scrim keys off it. */
    if (header) header.classList.add('is-menuopen');
    buttons.forEach(function (b) {
      b.setAttribute('aria-expanded', b.getAttribute('data-ef-menu') === key ? 'true' : 'false');
    });
    open = key;
  }

  /* Closing is an animation, not a disappearance. `hidden` is display:none,
     which cannot be transitioned, so the panel is marked is-closing, given the
     length of its own retract, and only then hidden. The duration is read off
     the element rather than written twice — change --ef-dur-menu-out and this
     follows it. */
  var exitTimer = null;

  function exitMs(el) {
    var v = getComputedStyle(el).getPropertyValue('--ef-dur-menu-out').trim();
    var n = parseFloat(v);
    if (!n) return 380;  /* matches --ef-dur-menu-out */
    return v.indexOf('ms') > -1 ? n : n * 1000;
  }

  function settle() {
    panels.forEach(function (p) { p.hidden = true; p.classList.remove('is-closing'); });
    exitTimer = null;
  }

  function hide() {
    clearTimeout(exitTimer);
    var live = panels.filter(function (p) { return !p.hidden; });
    if (header) header.classList.remove('is-menuopen');
    buttons.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
    open = null;
    if (!live.length) { settle(); return; }
    live.forEach(function (p) { p.classList.add('is-closing'); });
    exitTimer = setTimeout(settle, exitMs(live[0]));
  }

  buttons.forEach(function (btn) {
    var key = btn.getAttribute('data-ef-menu');
    btn.addEventListener('mouseenter', function () { show(key); });
    btn.addEventListener('focus', function () { show(key); });
    /* Touch has no hover, so the button is also a toggle. */
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      if (open === key) hide(); else show(key);
    });
  });

  panels.forEach(function (p) {
    p.addEventListener('mouseenter', function () { clearTimeout(closeTimer); });
  });

  /* Close on leaving the header, not on leaving the button: the pointer has to
     cross the gap between the two, and closing mid-journey is the classic
     mega-menu that cannot be used. */
  header.addEventListener('mouseleave', function () {
    closeTimer = setTimeout(hide, 120);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && open) {
      var btn = header.querySelector('[data-ef-menu="' + open + '"]');
      hide();
      if (btn) btn.focus();
    }
  });

  /* Focus moving out of the header closes it — tabbing past an open panel and
     leaving it hanging over the page is the keyboard equivalent of a stuck
     hover. */
  document.addEventListener('focusin', function (e) {
    if (open && !header.contains(e.target)) hide();
  });

  window.addEventListener('pagehide', hide);

  /* ------------------------------------------------- sub-category tab rows */
  panels.forEach(function (panel) {
    var tabs  = Array.prototype.slice.call(panel.querySelectorAll('[data-ef-menutab]'));
    var panes = Array.prototype.slice.call(panel.querySelectorAll('[data-ef-menupane]'));
    if (!tabs.length) return;
    function show(tab) {
      var i = tab.getAttribute('data-ef-menutab');
      tabs.forEach(function (t) {
        var on = t === tab;
        t.classList.toggle('is-on', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      panes.forEach(function (p) {
        p.classList.toggle('is-on', p.getAttribute('data-ef-menupane') === i);
      });
    }
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function (e) { e.preventDefault(); show(tab); });
      /* The whole menu opens on hover, so making the reader click to see a
         sub-range is an inconsistency inside one control. Pointer only:
         mouseenter does not fire for touch, and switching panes under a finger
         that is on its way to a card would steal the tap. Keyboard users still
         get it through focus. */
      tab.addEventListener('mouseenter', function () { show(tab); });
      tab.addEventListener('focus', function () { show(tab); });
    });
  });

  /* ------------------------------------------------------- phone drawer */
  /* EVERY [data-ef-burger], document-wide — not header.querySelector.
     The drawer carries its own close control and the drawer is a SIBLING of the
     header, so a header-scoped lookup never binds it. It also has to be every
     match rather than the first: with one binding the close button was inert and
     the drawer, which covers the burger, had no exit at all on a phone. */
  var burger = header.querySelector('[data-ef-burger]');
  var toggles = document.querySelectorAll('[data-ef-burger]');
  var mobnav = document.getElementById('ef-mobnav');
  if (toggles.length && mobnav) {
    var setOpen = function (open) {
      mobnav.hidden = !open;
      if (burger) burger.setAttribute('aria-expanded', open ? 'true' : 'false');
      /* The drawer is full-height and scrolls itself; the page behind it must
         not scroll with it. */
      document.documentElement.style.overflow = open ? 'hidden' : '';
      if (open) {
        var first = mobnav.querySelector('.ef-mobnav__close, a, button');
        if (first && first.focus) first.focus();
      } else {
        mobnav.dispatchEvent(new CustomEvent('ef:closed'));
        if (burger && burger.focus) burger.focus();
      }
    };
    Array.prototype.forEach.call(toggles, function (t) {
      t.addEventListener('click', function () { setOpen(mobnav.hidden); });
    });
    /* ---- drill-down screens ------------------------------------------
       Every screen is already in the DOM; navigating only moves which one
       carries .is-active. The screen being left keeps .is-before so it sits
       off-canvas to the LEFT rather than the right — otherwise going back
       would slide the old screen in from the wrong side. */
    var screens = mobnav.querySelector('.ef-mobnav__screens');
    var back = mobnav.querySelector('[data-mnav-back]');
    if (screens) {
      var rootScreen = screens.querySelector('#mnav-root');
      /* A STACK, not a boolean. Power Stations opens a screen of families and
         each family opens its own products, so Back has to walk up one level
         at a time — returning straight to root from the third level would
         throw away the choice the reader just made. */
      var stack = [rootScreen];

      function paint() {
        var current = stack[stack.length - 1];
        Array.prototype.forEach.call(
          screens.querySelectorAll('.ef-mobnav__screen'), function (s) {
            s.classList.remove('is-active', 'is-before');
          });
        /* Everything already visited sits off-canvas to the LEFT, so going back
           slides in from the correct side. */
        for (var i = 0; i < stack.length - 1; i++) stack[i].classList.add('is-before');
        current.classList.add('is-active');
        if (back) back.hidden = (stack.length < 2);
        current.scrollTop = 0;
        var first = current.querySelector('a, button');
        if (first && first.focus) first.focus({ preventScroll: true });
      }
      function push(el) { if (el) { stack.push(el); paint(); } }
      function pop() { if (stack.length > 1) { stack.pop(); paint(); } }
      function reset() { stack = [rootScreen]; paint(); }

      mobnav.addEventListener('click', function (ev) {
        var open = ev.target.closest && ev.target.closest('[data-mnav-open]');
        if (open) {
          ev.preventDefault();
          push(document.getElementById(open.getAttribute('data-mnav-open')));
          return;
        }
        if (ev.target.closest && ev.target.closest('[data-mnav-back]')) {
          ev.preventDefault();
          pop();
        }
      });
      /* Closing returns to the root, so reopening never resumes three levels
         deep in a category the reader has since forgotten choosing. */
      mobnav.addEventListener('ef:closed', reset);
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !mobnav.hidden) setOpen(false);
    });
    /* Tap outside to dismiss. The scrim only reacts to .ef-header.is-menuopen,
       a class this handler never sets, so without this the drawer had no
       outside-dismissal at all — which is why the missing close control was a
       dead end rather than an inconvenience. */
    document.addEventListener('click', function (e) {
      if (mobnav.hidden) return;
      if (mobnav.contains(e.target)) return;
      if (e.target.closest && e.target.closest('[data-ef-burger]')) return;
      setOpen(false);
    });
  }
})();
